/**
 * Användarmanual
 * Samma sektioner som vänstermenyn. Ett val leder vidare, och sista steget är text.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useLanguage, useTranslation } from '@/store/languageStore';
import { ArrowLeftIcon, BookOpenIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useUserMenu } from '@/hooks/useUserManual';
import { moduleManualCopy } from '@/content/moduleManual';
import {
  collectMenuSearchItems,
  filterMenuSearchItems,
  manualNextStep,
  resolveManualBody,
} from '@/utils/menuSearch';
import { tModule } from '@/utils/moduleLabels';
import { getIconColors, getModuleIcon } from '@/utils/moduleIcons';
import { groupModules, groupSidebarSystems } from '@/utils/navGroups';
import type { MenuModule, MenuSystem } from '@/types/systems';

type Frame =
  | { kind: 'system'; systemName: string }
  | { kind: 'children'; systemName: string; moduleName: string }
  | { kind: 'text'; systemName: string; moduleName: string | null }
  | { kind: 'guide' };

function topModules(system: MenuSystem): MenuModule[] {
  return (system.modules || []).filter((module) => module.name !== 'live-tracking');
}

function findModule(modules: MenuModule[], name: string): MenuModule | undefined {
  for (const module of modules) {
    if (module.name === name) return module;
    const child = findModule(module.children || [], name);
    if (child) return child;
  }
  return undefined;
}

const UserManual = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { systems, loading } = useUserMenu();
  const [searchQuery, setSearchQuery] = useState('');
  const [stack, setStack] = useState<Frame[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stack]);

  const frame = stack[stack.length - 1];
  const system = systems.find((item) => item.name === (frame && 'systemName' in frame ? frame.systemName : ''));

  const menuSections = useMemo(
    () =>
      groupSidebarSystems(
        systems,
        (item) => tModule(t, item.name, 'title') || item.displayName,
        language
      ),
    [systems, t, language]
  );

  const searchHits = useMemo(() => {
    const labeled = collectMenuSearchItems(systems).map((entry) => {
      const title = entry.moduleName
        ? tModule(t, entry.moduleName, 'title', entry.systemName) || entry.fallbackTitle
        : tModule(t, entry.systemName, 'title') || entry.fallbackTitle;
      const description = manualBody(entry.systemName, entry.moduleName, title, '');
      const systemTitle = tModule(t, entry.systemName, 'title') || entry.systemName;
      return { ...entry, title, systemTitle, description };
    });
    return filterMenuSearchItems(labeled, searchQuery);
  }, [systems, searchQuery, t, language]);

  const listModules = useMemo(() => {
    if (!system || !frame) return [];
    if (frame.kind === 'system') return topModules(system);
    if (frame.kind === 'children') {
      return findModule(system.modules || [], frame.moduleName)?.children || [];
    }
    return [];
  }, [system, frame]);

  const moduleSections = useMemo(() => {
    if (!system || (frame?.kind !== 'system' && frame?.kind !== 'children')) return [];
    return groupModules(
      system.name,
      listModules,
      (module) => tModule(t, module.name, 'title', system.name) || module.displayName,
      language
    );
  }, [system, frame, listModules, t, language]);

  const goBack = () => {
    if (stack.length === 0) {
      navigate(ROUTES.HOME);
      return;
    }
    setStack((current) => current.slice(0, -1));
  };

  const openSystem = (item: MenuSystem) => {
    if (manualNextStep(topModules(item).length) === 'list') {
      setStack([{ kind: 'system', systemName: item.name }]);
      return;
    }
    setStack([{ kind: 'text', systemName: item.name, moduleName: null }]);
  };

  const openModule = (systemName: string, module: MenuModule) => {
    const next: Frame =
      manualNextStep(module.children?.length || 0) === 'list'
        ? { kind: 'children', systemName, moduleName: module.name }
        : { kind: 'text', systemName, moduleName: module.name };
    setStack((current) => [...current, next]);
  };

  const article = (() => {
    if (frame?.kind !== 'text' || !system) return null;
    if (!frame.moduleName) {
      const title = tModule(t, system.name, 'title') || system.displayName;
      return { title, body: manualBody(system.name, null, title, t('manual.noDescription')) };
    }
    const module = findModule(system.modules || [], frame.moduleName);
    const title = tModule(t, frame.moduleName, 'title', system.name) || module?.displayName || frame.moduleName;
    return { title, body: manualBody(system.name, frame.moduleName, title, t('manual.noDescription')) };
  })();

  function manualBody(systemName: string, moduleName: string | null, title: string, emptyText: string): string {
    const name = moduleName || systemName;
    return resolveManualBody(
      moduleManualCopy(language, name, moduleName ? systemName : null),
      moduleName
        ? tModule(t, moduleName, 'description', systemName)
        : tModule(t, systemName, 'description'),
      title,
      emptyText
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={goBack} className="p-2 hover:bg-bgElevated rounded-lg transition-colors" aria-label={t('common.back')}>
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <BookOpenIcon className="w-6 h-6 text-primary" />
          {t('manual.title')}
        </h1>
      </div>

      {!frame && <p className="text-sm text-textSecondary mb-4">{t('manual.intro')}</p>}

      {!frame && (
        <div className="mb-6 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-textTertiary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t('manual.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bgElevated text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-textTertiary">{t('common.loading')}</div>
      ) : frame?.kind === 'guide' ? (
        <GuideText />
      ) : article ? (
        <article>
          <h2 className="text-xl font-semibold text-textDefault mb-4">{article.title}</h2>
          <p className="text-base leading-7 text-textDefault">{article.body}</p>
        </article>
      ) : frame?.kind === 'system' || frame?.kind === 'children' ? (
        <div className="space-y-6">
          {moduleSections.map((section) => (
            <section key={section.id}>
              <h2 className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-textTertiary">
                {t(section.labelKey)}
              </h2>
              <div className="border border-border rounded-lg bg-bgContainer divide-y divide-border">
                {section.items.map((module) => (
                  <ChoiceButton
                    key={module.id}
                    icon={module.icon}
                    label={tModule(t, module.name, 'title', system?.name) || module.displayName}
                    onClick={() => system && openModule(system.name, module)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : searchQuery.trim() ? (
        <div className="border border-border rounded-lg bg-bgContainer divide-y divide-border">
          {searchHits.length === 0 ? (
            <p className="px-4 py-6 text-sm text-textTertiary">{t('manual.noResults')}</p>
          ) : (
            searchHits.map((hit) => (
              <ChoiceButton
                key={hit.key}
                icon={hit.icon}
                label={hit.title}
                hint={hit.systemTitle}
                onClick={() => setStack([{ kind: 'text', systemName: hit.systemName, moduleName: hit.moduleName }])}
              />
            ))
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {menuSections.map((section) => (
            <section key={section.id}>
              <h2 className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-textTertiary">
                {t(section.labelKey)}
              </h2>
              <div className="border border-border rounded-lg bg-bgContainer divide-y divide-border">
                {section.items.map((item) => (
                  <ChoiceButton
                    key={item.id}
                    icon={item.icon}
                    label={tModule(t, item.name, 'title') || item.displayName}
                    onClick={() => openSystem(item)}
                  />
                ))}
              </div>
            </section>
          ))}
          <section>
            <h2 className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-textTertiary">
              {t('manual.guides.title')}
            </h2>
            <div className="border border-border rounded-lg bg-bgContainer">
              <ChoiceButton
                label={t('manual.guides.deviceVehicleTitle')}
                onClick={() => setStack([{ kind: 'guide' }])}
              />
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

function ChoiceButton({
  icon,
  label,
  hint,
  onClick,
}: {
  icon?: string;
  label: string;
  hint?: string;
  onClick: () => void;
}) {
  const colors = getIconColors(icon);
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-bgElevated">
      {icon && (
        <span className={`p-1.5 rounded-md ${colors.bgClass} ${colors.textClass}`}>
          {getModuleIcon(icon, 'w-5 h-5')}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-textDefault">{label}</span>
        {hint && <span className="block text-sm text-textTertiary">{hint}</span>}
      </span>
      <ChevronRightIcon className="w-4 h-4 text-textTertiary shrink-0" />
    </button>
  );
}

function GuideText() {
  const { t } = useTranslation();
  return (
    <article>
      <h2 className="text-xl font-semibold text-textDefault mb-2">{t('manual.guides.deviceVehicleTitle')}</h2>
      <p className="text-base leading-7 text-textDefault mb-4">{t('manual.guides.deviceVehicleIntro')}</p>
      {(['auto', 'manual'] as const).map((block) => {
        const steps = t(`manual.guides.${block}Steps`, { returnObjects: true }) as unknown;
        const stepList = Array.isArray(steps) ? (steps as string[]) : [];
        return (
          <div key={block} className="mb-4">
            <h3 className="font-medium text-textDefault mb-1">{t(`manual.guides.${block}Heading`)}</h3>
            <ul className="list-disc list-inside text-base leading-7 text-textDefault space-y-1">
              {stepList.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </article>
  );
}

export default UserManual;
